import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAdeProfiles } from "@/hooks/useAdeProfiles";
import { useAdeDevices } from "@/hooks/useAdeDevices";
import { useAbmConnections } from "@/hooks/useAbmConnections";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle, AlertTriangle, FileText, Settings2, ShieldCheck, Phone, MonitorSmartphone, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { buildEnrollmentProfileActions } from "@/components/actions";
import { Link } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Setup Assistant items mapping
const SKIP_SETUP_ITEMS_MAP: Record<string, string> = {
  Location: "Location Services",
  Restore: "Restore from Backup",
  Android: "Android Migration",
  AppleID: "Apple ID Setup",
  Terms: "Terms & Conditions",
  Siri: "Siri",
  Diagnostics: "Diagnostics",
  DisplayTone: "Display Tone",
  Passcode: "Passcode",
  Biometric: "Touch ID / Face ID",
  Payment: "Apple Pay",
  Zoom: "Zoom",
  ScreenTime: "Screen Time",
  SoftwareUpdate: "Software Update",
  Watch: "Apple Watch Setup",
  FileVault: "FileVault",
  iCloudDiagnostics: "iCloud Diagnostics",
  AppStore: "App Store",
  Accessibility: "Accessibility",
};

export default function EnrollmentProfileDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { connections } = useAbmConnections();
  const firstAccountId = connections.length > 0 ? connections[0].id : undefined;
  const { profiles, deleteProfile, loading } = useAdeProfiles(firstAccountId);
  const { devices, getDevicesByProfile } = useAdeDevices(firstAccountId);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const profile = profiles.find(p => p.id === id);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Profile Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The enrollment profile you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate("/profiles")}>
            Back to Profiles
          </Button>
        </div>
      </div>
    );
  }

  // Get assigned devices for this profile
  const assignedDevices = profile.profile_uuid 
    ? getDevicesByProfile(profile.profile_uuid)
    : [];

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      // Hook surfaces success/error toasts; we just navigate on success.
      await deleteProfile(profile.id);
      navigate("/profiles");
    } catch {
      // Hook already toasted the failure.
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const skipSetupItems = (profile.skip_setup_items as string[]) || [];
  const hasDevices = assignedDevices.length > 0;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b bg-card/30">
        {/* Row 1: Navigation and Actions */}
        <div className="flex items-center justify-between px-6 py-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate("/profiles?tab=enrollment")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Profiles
          </Button>
          
          <ActionsDropdown
            align="end"
            actions={buildEnrollmentProfileActions({
              surface: "detail",
              onView: () => {},
              onEdit: () => navigate(`/profiles/enrollment/${profile.id}/edit`),
              onDelete: handleDelete,
            })}
          />
        </div>

        {/* Row 2: Profile Details */}
        <div className="px-6 pb-8 pt-4">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center shadow-sm">
              <FileText className="h-8 w-8 text-primary" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-semibold">{profile.profile_name}</h1>
              {profile.description && (
                <p className="text-sm text-muted-foreground mt-1">{profile.description}</p>
              )}
              <div className="flex items-center space-x-3 mt-3">
                <Badge variant="outline" className="text-info-muted-foreground border-info-border">
                  {profile.abm_connection?.name || "ABM Connected"}
                </Badge>
                {profile.department && (
                  <Badge variant="secondary">{profile.department}</Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  Created {format(new Date(profile.created_at!), "MMM d, yyyy")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      {profile.is_draft ? (
        <div className="border-b px-6 py-4 bg-info-muted border-info-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-info" />
              <div>
                <p className="font-medium text-info-muted-foreground">Draft</p>
                <p className="text-sm text-info-muted-foreground/80">
                  No devices assigned yet. Assign devices to activate this profile.
                </p>
              </div>
            </div>
            <Button variant="default" size="sm" asChild>
              <Link to={`/profiles/enrollment/${profile.id}/edit`}>
                Assign Devices
              </Link>
            </Button>
          </div>
        </div>
      ) : (
        <div className={`border-b px-6 py-3 ${!hasDevices ? 'bg-warning-muted border-warning-border' : 'bg-success-muted border-success-border'}`}>
          <div className="flex items-center gap-3">
            {!hasDevices ? (
              <AlertTriangle className="h-5 w-5 text-warning" />
            ) : (
              <CheckCircle className="h-5 w-5 text-success" />
            )}
            <div>
              <p className="font-medium">
                {!hasDevices ? "No Devices Assigned" : "Deployed"}
              </p>
              <p className="text-sm text-muted-foreground">
                {!hasDevices 
                  ? "This profile is not yet assigned to any devices"
                  : `Assigned to ${assignedDevices.length} device${assignedDevices.length !== 1 ? 's' : ''}`
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* General Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Settings2 className="h-4 w-4" />
                    General Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Profile Name</label>
                    <p className="font-medium">{profile.profile_name}</p>
                  </div>
                  
                  {profile.description && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Description</label>
                      <p className="text-sm">{profile.description}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">ABM Connection</label>
                    <p className="text-sm">{profile.abm_connection?.name || "Unknown"}</p>
                    {profile.abm_connection?.organization_id && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Org ID: {profile.abm_connection.organization_id}
                      </p>
                    )}
                  </div>

                  {profile.department && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Department</label>
                      <p className="text-sm">{profile.department}</p>
                    </div>
                  )}

                  {profile.profile_uuid && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Profile UUID</label>
                      <p className="text-xs font-mono text-muted-foreground break-all">{profile.profile_uuid}</p>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-6 pt-2 border-t">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Created</label>
                      <p className="text-sm">{format(new Date(profile.created_at!), "MMM d, yyyy")}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Last Modified</label>
                      <p className="text-sm">{format(new Date(profile.updated_at!), "MMM d, yyyy")}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Skip Setup Screens */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <MonitorSmartphone className="h-4 w-4" />
                    Setup Assistant Configuration
                    {skipSetupItems.length > 0 && (
                      <Badge variant="secondary" className="ml-auto">
                        {skipSetupItems.length} screen{skipSetupItems.length !== 1 ? 's' : ''} hidden
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Setup screens that will be hidden during device enrollment
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {skipSetupItems.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <p className="text-sm">All setup screens will be shown to users</p>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {skipSetupItems.map((item) => (
                        <Badge key={item} variant="outline">
                          {SKIP_SETUP_ITEMS_MAP[item] || item}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* MDM Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4" />
                    MDM Settings
                  </CardTitle>
                  <CardDescription>
                    Enrollment and management options
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Mandatory Enrollment</span>
                    <Badge variant={profile.is_mandatory ? "default" : "secondary"}>
                      {profile.is_mandatory ? "Yes" : "No"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">MDM Removable</span>
                    <Badge variant={profile.is_mdm_removable ? "default" : "secondary"}>
                      {profile.is_mdm_removable ? "Yes" : "No"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Wait for Configuration</span>
                    <Badge variant={profile.await_device_configured ? "default" : "secondary"}>
                      {profile.await_device_configured ? "Yes" : "No"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Auto-Advance Setup</span>
                    <Badge variant={profile.auto_advance_setup ? "default" : "secondary"}>
                      {profile.auto_advance_setup ? "Yes" : "No"}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Support Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Support Information
                  </CardTitle>
                  <CardDescription>
                    Contact details shown during enrollment
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Phone Number</label>
                    <p className="text-sm">
                      {profile.support_phone_number || (
                        <span className="text-muted-foreground italic">Not configured</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email Address</label>
                    <p className="text-sm">
                      {profile.support_email_address || (
                        <span className="text-muted-foreground italic">Not configured</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Web URL</label>
                    {profile.configuration_web_url ? (
                      <a 
                        href={profile.configuration_web_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline break-all"
                      >
                        {profile.configuration_web_url}
                      </a>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">Not configured</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Assigned Devices */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MonitorSmartphone className="h-4 w-4" />
                Assigned Devices
                <Badge variant="secondary" className="ml-auto">
                  {assignedDevices.length} device{assignedDevices.length !== 1 ? 's' : ''}
                </Badge>
              </CardTitle>
              <CardDescription>
                Devices that will use this enrollment profile
              </CardDescription>
            </CardHeader>
            <CardContent>
              {assignedDevices.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MonitorSmartphone className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No devices assigned to this profile</p>
                  <p className="text-sm mt-1">
                    Edit the profile to assign devices from your ABM account
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Serial Number</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>OS</TableHead>
                      <TableHead>Assigned Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignedDevices.map((device) => (
                      <TableRow key={device.id}>
                        <TableCell className="font-medium">{device.serial_number}</TableCell>
                        <TableCell>{device.model || "Unknown"}</TableCell>
                        <TableCell>{device.os || "Unknown"}</TableCell>
                        <TableCell>
                          {device.profile_assign_time 
                            ? format(new Date(device.profile_assign_time), "MMM d, yyyy")
                            : "—"
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Enrollment Profile</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{profile.profile_name}"? This action cannot be undone.
              {hasDevices && (
                <>
                  <br /><br />
                  <strong>This profile is currently assigned to {assignedDevices.length} device{assignedDevices.length !== 1 ? 's' : ''}.</strong>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete Profile"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
